(function() {
  'use strict';

  const MultiLocationScreenshotService = {

    hiddenCanvases: new Map(),

    captureAllLocations: async function(config) {
      const { locationIds, state, getLocation, applyThreadColors, getDefaultFont, loadFont } = config;

      const capturePromises = locationIds.map((locationId, index) => {
        return new Promise(resolve => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              this.captureLocation({
                locationId,
                location: getLocation(locationId),
                locState: state[locationId],
                applyThreadColors,
                getDefaultFont,
                loadFont
              }).then(resolve).catch(error => {
                resolve({
                  locationId,
                  location: getLocation(locationId),
                  locState: state[locationId],
                  blobs: { fullMockup: null, logo: null, text: null },
                  error: error.message
                });
              });
            }, index * 50);
          });
        });
      });

      const captures = await Promise.all(capturePromises);
      return captures.filter(c => c.blobs.fullMockup !== null);
    },

    captureLocation: async function(config) {
      const { locationId, location, locState, applyThreadColors, getDefaultFont, loadFont } = config;

      if (!location) {
        throw new Error(`Location ${locationId} not found`);
      }

      const { hiddenCanvas, hiddenFabric } = this.createHiddenFabricCanvas(locationId);

      try {
        await this.renderProductImageToCanvas(hiddenFabric, location);

        if (locState.selectedLogo) {
          await this.renderLogoToCanvas(hiddenFabric, location, locState, applyThreadColors);
        }

        if (locState.personalization?.text?.trim()) {
          await this.renderTextToCanvas(hiddenFabric, location, locState, getDefaultFont, loadFont);
        }

        const fullMockupBlob = await this.captureFabricCanvasToBlob(hiddenFabric);

        let logoBlob = null;
        if (locState.selectedLogo) {
          logoBlob = await this.captureLogoOnly(locState, location, applyThreadColors);
        }

        let textBlob = null;
        if (locState.personalization?.text?.trim()) {
          textBlob = await this.captureTextOnly(locState, getDefaultFont, loadFont);
        }

        return {
          locationId,
          location,
          locState,
          blobs: { fullMockup: fullMockupBlob, logo: logoBlob, text: textBlob }
        };

      } finally {
        this.destroyHiddenCanvas(locationId);
      }
    },

    createHiddenFabricCanvas: function(locationId) {
      const hiddenCanvas = document.createElement('canvas');
      hiddenCanvas.id = `screenshot-service-hidden-canvas-${locationId}`;
      hiddenCanvas.width = 800;
      hiddenCanvas.height = 800;
      hiddenCanvas.style.position = 'absolute';
      hiddenCanvas.style.left = '-9999px';
      hiddenCanvas.style.top = '-9999px';
      hiddenCanvas.style.pointerEvents = 'none';
      document.body.appendChild(hiddenCanvas);

      const hiddenFabric = new fabric.Canvas(hiddenCanvas, {
        selection: false,
        renderOnAddRemove: false,
        skipOffscreen: false
      });

      this.hiddenCanvases.set(locationId, { hiddenCanvas, hiddenFabric });
      return { hiddenCanvas, hiddenFabric };
    },

    renderProductImageToCanvas: async function(hiddenFabric, location) {
      const imageUrl = location.picture || location.fallbackImage;

      return new Promise((resolve, reject) => {
        fabric.Image.fromURL(imageUrl, (img) => {
          if (!img) {
            reject(new Error('Failed to load product image'));
            return;
          }

          const scale = Math.min(
            hiddenFabric.width / img.width,
            hiddenFabric.height / img.height
          );

          img.set({
            left: hiddenFabric.width / 2,
            top: hiddenFabric.height / 2,
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
            selectable: false
          });

          hiddenFabric.add(img);
          hiddenFabric.sendToBack(img);
          hiddenFabric.renderAll();
          requestAnimationFrame(() => resolve());
        }, { crossOrigin: 'anonymous' });
      });
    },

    renderLogoToCanvas: async function(hiddenFabric, location, locState, applyThreadColors) {
      const selectedLogo = locState.selectedLogo;
      const logoUrl = selectedLogo.url;
      const savedPosition = locState.logoPosition;
      const customColors = locState.logoThreadColors?.[selectedLogo.id] || {};

      const response = await fetch(logoUrl);
      let svgText = await response.text();
      svgText = applyThreadColors(svgText, location.threadInstructions, location.switchy, customColors);

      return new Promise((resolve) => {
        fabric.loadSVGFromString(svgText, (objects, options) => {
          const logoGroup = fabric.util.groupSVGElements(objects, options);

          if (savedPosition) {
            const originalCanvasWidth = savedPosition.canvasWidth || 800;
            const originalCanvasHeight = savedPosition.canvasHeight || 800;
            const scaleFactorX = hiddenFabric.width / originalCanvasWidth;
            const scaleFactorY = hiddenFabric.height / originalCanvasHeight;

            logoGroup.set({
              left: savedPosition.left * scaleFactorX,
              top: savedPosition.top * scaleFactorY,
              scaleX: savedPosition.scaleX * scaleFactorX,
              scaleY: savedPosition.scaleY * scaleFactorY,
              angle: savedPosition.angle || 0,
              originX: 'left',
              originY: 'top',
              selectable: false
            });
          } else {
            logoGroup.set({
              left: hiddenFabric.width / 2,
              top: hiddenFabric.height / 2,
              originX: 'center',
              originY: 'center',
              selectable: false
            });
          }

          hiddenFabric.add(logoGroup);
          hiddenFabric.renderAll();
          requestAnimationFrame(() => resolve());
        });
      });
    },

    renderTextToCanvas: async function(hiddenFabric, location, locState, getDefaultFont, loadFont) {
      const personalization = locState.personalization;
      const text = personalization.text.trim();
      const font = personalization.font || getDefaultFont();
      const threadColor = personalization.threadColor || { hex: '#FFFFFF' };
      const fillColor = threadColor.hex.startsWith('#') ? threadColor.hex : `#${threadColor.hex}`;
      const savedPosition = personalization.position;

      await loadFont(font);

      const originalCanvasWidth = savedPosition?.canvasWidth || 800;
      const originalCanvasHeight = savedPosition?.canvasHeight || 800;
      const scaleFactorX = hiddenFabric.width / originalCanvasWidth;
      const scaleFactorY = hiddenFabric.height / originalCanvasHeight;
      const scaleFactor = Math.min(scaleFactorX, scaleFactorY);

      return new Promise((resolve) => {
        const textObject = new fabric.Text(text, {
          fontFamily: font.family,
          fill: fillColor,
          fontSize: (savedPosition?.fontSize || 40) * scaleFactor,
          left: savedPosition?.left ? savedPosition.left * scaleFactorX : hiddenFabric.width / 2,
          top: savedPosition?.top ? savedPosition.top * scaleFactorY : hiddenFabric.height / 2,
          angle: savedPosition?.angle || 0,
          originX: 'center',
          originY: 'center',
          textAlign: personalization.textAlign || 'center',
          selectable: false
        });

        textObject.isText = true;
        hiddenFabric.add(textObject);
        hiddenFabric.renderAll();
        requestAnimationFrame(() => resolve());
      });
    },

    captureFabricCanvasToBlob: async function(hiddenFabric) {
      const dataUrl = hiddenFabric.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 2.56
      });
      const blob = await fetch(dataUrl).then(r => r.blob());
      return blob;
    },

    captureLogoOnly: async function(locState, location, applyThreadColors) {
      const selectedLogo = locState.selectedLogo;
      const customColors = locState.logoThreadColors?.[selectedLogo.id] || {};

      const response = await fetch(selectedLogo.url);
      let svgText = await response.text();
      svgText = applyThreadColors(svgText, location.threadInstructions, location.switchy, customColors);

      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 1024;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 1024, 1024);

      return new Promise((resolve, reject) => {
        const img = new Image();
        const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          ctx.drawImage(img, 0, 0, 1024, 1024);
          URL.revokeObjectURL(url);
          canvas.toBlob((blob) => { resolve(blob); }, 'image/png', 0.95);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load logo SVG'));
        };

        img.src = url;
      });
    },

    captureTextOnly: async function(locState, getDefaultFont, loadFont) {
      const personalization = locState.personalization;
      const text = personalization.text.trim();
      const font = personalization.font || getDefaultFont();
      const threadColor = personalization.threadColor || { hex: '#FFFFFF' };
      const fillColor = threadColor.hex.startsWith('#') ? threadColor.hex : `#${threadColor.hex}`;

      await loadFont(font);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const fontSize = personalization.position?.fontSize || 40;
      ctx.font = `${fontSize}px ${font.family}`;
      const metrics = ctx.measureText(text);
      const textWidth = metrics.width;
      const textHeight = fontSize * 1.2;

      canvas.width = Math.ceil(textWidth + 40);
      canvas.height = Math.ceil(textHeight + 40);

      ctx.font = `${fontSize}px ${font.family}`;
      ctx.fillStyle = fillColor;
      ctx.textAlign = personalization.textAlign || 'center';
      ctx.textBaseline = 'middle';

      const x = personalization.textAlign === 'center' ? canvas.width / 2 :
                personalization.textAlign === 'right' ? canvas.width - 20 : 20;
      const y = canvas.height / 2;

      ctx.fillText(text, x, y);

      return new Promise((resolve) => {
        canvas.toBlob((blob) => { resolve(blob); }, 'image/png', 0.95);
      });
    },

    destroyHiddenCanvas: function(locationId) {
      const canvasData = this.hiddenCanvases.get(locationId);
      if (canvasData) {
        const { hiddenCanvas, hiddenFabric } = canvasData;
        hiddenFabric.dispose();
        if (hiddenCanvas.parentNode) {
          hiddenCanvas.parentNode.removeChild(hiddenCanvas);
        }
        this.hiddenCanvases.delete(locationId);
      }
      if (window.gc) {
        try { window.gc(); } catch (e) {}
      }
    },

    _isCapturing: false,
    _deviceInfo: null,

    _pipelineConfig: {
      logoSelectorInput: 'input[name="properties[selected_logo]"]:checked',
      captureDelay: 800,
      memoryManagement: {
        maxCanvasSize: 16777216,
        cleanupDelay: 100,
        enableGC: true
      }
    },

    detectDevice: function() {
      if (this._deviceInfo) return this._deviceInfo;

      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                       (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
      const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(navigator.userAgent);
      const isDesktop = !isMobile && !isTablet;

      const deviceMemory = navigator.deviceMemory || 4;
      const hardwareConcurrency = navigator.hardwareConcurrency || 4;
      const pixelRatio = window.devicePixelRatio || 1;

      let performanceClass = 'medium';
      if (deviceMemory >= 8 && hardwareConcurrency >= 8) {
        performanceClass = 'high';
      } else if (deviceMemory <= 2 || hardwareConcurrency <= 2) {
        performanceClass = 'low';
      }

      this._deviceInfo = {
        isMobile, isTablet, isDesktop,
        deviceMemory, hardwareConcurrency, pixelRatio, performanceClass,
        viewport: { width: window.innerWidth, height: window.innerHeight }
      };

      return this._deviceInfo;
    },

    getAdaptiveConfig: function() {
      const device = this.detectDevice();

      const configs = {
        mobile: {
          low: { productScale: 1.5, logoScale: 1, captureDelay: 1200, maxRetries: 1 },
          medium: { productScale: 2, logoScale: 1.5, captureDelay: 1000, maxRetries: 2 },
          high: { productScale: 3, logoScale: 2, captureDelay: 800, maxRetries: 3 }
        },
        tablet: {
          low: { productScale: 2, logoScale: 1.5, captureDelay: 1000, maxRetries: 2 },
          medium: { productScale: 3, logoScale: 2, captureDelay: 900, maxRetries: 2 },
          high: { productScale: 4, logoScale: 2.5, captureDelay: 800, maxRetries: 3 }
        },
        desktop: {
          low: { productScale: 3, logoScale: 2, captureDelay: 900, maxRetries: 2 },
          medium: { productScale: 4, logoScale: 2.5, captureDelay: 800, maxRetries: 3 },
          high: { productScale: 6, logoScale: 3, captureDelay: 600, maxRetries: 3 }
        }
      };

      let deviceType = 'desktop';
      if (device.isMobile) deviceType = 'mobile';
      else if (device.isTablet) deviceType = 'tablet';

      return configs[deviceType][device.performanceClass];
    },

    memoryUtils: {
      cleanupCanvas: function(canvas) {
        if (canvas && canvas.getContext) {
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
          canvas.width = 0;
          canvas.height = 0;
        }
      },
      cleanupBlob: function(blob) {
        if (blob && blob.stream) {
          try { blob.stream().cancel(); } catch (e) {}
        }
      },
      revokeObjectURL: function(url) {
        if (url && url.startsWith('blob:')) {
          try { URL.revokeObjectURL(url); } catch (e) {}
        }
      },
      forceGarbageCollection: function() {
        if (window.gc) {
          try { window.gc(); } catch (e) {}
        }
      },
      checkMemoryPressure: function() {
        if (navigator.deviceMemory && navigator.deviceMemory <= 2) return 'high';
        var device = MultiLocationScreenshotService.detectDevice();
        if (device.isMobile && device.performanceClass === 'low') return 'medium';
        return 'low';
      }
    },

    getSelectedImageWrapper: function() {
      var device = this.detectDevice();

      var selectedCell = document.querySelector('.product__media.is-selected');

      if (!selectedCell) {
        var allCells = document.querySelectorAll('.product__media.flickity-cell');
        selectedCell = Array.from(allCells).find(function(el) {
          return el.getAttribute('aria-hidden') === 'false';
        });
      }

      if (!selectedCell) {
        var allMedia = document.querySelectorAll('.product__media');
        selectedCell = Array.from(allMedia).find(function(el) {
          return window.getComputedStyle(el).opacity === '1';
        });
      }

      if (!selectedCell) {
        var flickityCells = document.querySelectorAll('.product__media.flickity-cell');
        selectedCell = Array.from(flickityCells).find(function(el) {
          var rect = el.getBoundingClientRect();
          if (device.isMobile) {
            var tolerance = 50;
            return rect.left >= -tolerance && rect.left < (window.innerWidth + tolerance);
          }
          return rect.left >= 0 && rect.left < window.innerWidth;
        });
      }

      if (!selectedCell && device.isMobile) {
        var visibleMedia = document.querySelectorAll('.product__media');
        selectedCell = Array.from(visibleMedia).find(function(el) {
          var rect = el.getBoundingClientRect();
          return rect.top >= 0 && rect.top < window.innerHeight && rect.width > 0 && rect.height > 0;
        });
      }

      if (selectedCell) {
        var wrapper = selectedCell.querySelector('.product-image-wrapper');
        if (wrapper && device.isMobile) {
          var wrapperRect = wrapper.getBoundingClientRect();
          if (wrapperRect.width === 0 || wrapperRect.height === 0) return null;
        }
        return wrapper;
      }

      return null;
    },

    getSelectedLogoElement: function() {
      var logoInput = document.querySelector(this._pipelineConfig.logoSelectorInput);
      if (!logoInput) return null;

      var logoItem = logoInput.closest('.product-logo-selector__item');
      if (!logoItem) return null;

      return logoItem.querySelector('.product-logo-selector__svg');
    },

    captureElement: async function(element) {
      if (!element) throw new Error('No element provided to capture');

      var memoryPressure = this.memoryUtils.checkMemoryPressure();
      var adaptiveConfig = this.getAdaptiveConfig();

      if (!element.isConnected || !document.contains(element)) {
        throw new Error('Element is no longer connected to document');
      }

      var rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        throw new Error('Element has zero dimensions: ' + rect.width + 'x' + rect.height);
      }

      var canvas = null;

      try {
        var productImage = element.querySelector('img');
        var logoOverlay = element.querySelector('.logo-universal-v2');

        if (!productImage) throw new Error('No product image found');

        canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d');
        var scale = Math.min(adaptiveConfig.productScale, 2);

        canvas.width = rect.width * scale;
        canvas.height = rect.height * scale;

        var loadImage = function(src) {
          return new Promise(function(resolve, reject) {
            var img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function() { resolve(img); };
            img.onerror = reject;
            img.src = src;
          });
        };

        var productImg = await loadImage(productImage.src);
        ctx.drawImage(productImg, 0, 0, canvas.width, canvas.height);

        if (logoOverlay) {
          var logoSvg = logoOverlay.querySelector('svg');
          if (logoSvg) {
            var logoStyle = logoOverlay.style;
            var left = parseFloat(logoStyle.left) || 0;
            var top = parseFloat(logoStyle.top) || 0;
            var width = parseFloat(logoStyle.width) || 50;
            var height = parseFloat(logoStyle.height) || 50;

            var relX = left * scale;
            var relY = top * scale;
            var logoWidth = width * scale;
            var logoHeight = height * scale;

            var svgData = new XMLSerializer().serializeToString(logoSvg);
            var svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            var svgUrl = URL.createObjectURL(svgBlob);

            try {
              var logoImg = await loadImage(svgUrl);
              ctx.drawImage(logoImg, relX, relY, logoWidth, logoHeight);
              URL.revokeObjectURL(svgUrl);
            } catch (logoError) {}
          }
        }
      } catch (nativeError) {
        if (typeof html2canvas !== 'undefined') {
          canvas = await html2canvas(element, {
            useCORS: true,
            allowTaint: true,
            scale: 1,
            logging: false,
            silent: true,
            ignoreElements: function(el) {
              var tag = el.tagName ? el.tagName.toLowerCase() : '';
              return tag.includes('-') || ['script', 'style', 'input', 'button'].includes(tag);
            }
          });
        } else {
          throw nativeError;
        }
      }

      var canvasSize = canvas.width * canvas.height * 4;
      if (canvasSize > this._pipelineConfig.memoryManagement.maxCanvasSize) {
        var ratio = Math.sqrt(this._pipelineConfig.memoryManagement.maxCanvasSize / canvasSize);
        var newCanvas = document.createElement('canvas');
        newCanvas.width = Math.floor(canvas.width * ratio);
        newCanvas.height = Math.floor(canvas.height * ratio);
        var newCtx = newCanvas.getContext('2d');
        newCtx.drawImage(canvas, 0, 0, newCanvas.width, newCanvas.height);
        this.memoryUtils.cleanupCanvas(canvas);
        canvas = newCanvas;
      }

      var self = this;
      return new Promise(function(resolve, reject) {
        var cleanup = function() {
          setTimeout(function() {
            if (canvas) {
              self.memoryUtils.cleanupCanvas(canvas);
              canvas = null;
            }
            if (self._pipelineConfig.memoryManagement.enableGC) {
              self.memoryUtils.forceGarbageCollection();
            }
          }, self._pipelineConfig.memoryManagement.cleanupDelay);
        };

        canvas.toBlob(function(blob) {
          if (blob) {
            cleanup();
            resolve(blob);
          } else {
            cleanup();
            reject(new Error('Failed to create blob from canvas'));
          }
        }, 'image/png', memoryPressure === 'high' ? 0.8 : 0.95);
      });
    },

    captureLogoSvg: async function(logoElement) {
      if (!logoElement) throw new Error('No logo element provided');

      var svgElement = logoElement.querySelector('svg');
      if (!svgElement) throw new Error('No SVG found inside logo element');

      var adaptiveConfig = this.getAdaptiveConfig();
      var memoryPressure = this.memoryUtils.checkMemoryPressure();

      var dimensions = 500;
      if (memoryPressure === 'high') dimensions = 300;
      else if (memoryPressure === 'medium') dimensions = 400;
      dimensions = Math.floor(dimensions * adaptiveConfig.logoScale / 3);

      var svgBlob = null;
      var objectURL = null;
      var canvas = null;
      var self = this;

      return new Promise(function(resolve, reject) {
        try {
          var serializer = new XMLSerializer();
          var svgString = serializer.serializeToString(svgElement);

          var textureMatch = svgString.match(/<image[^>]*style="[^"]*mix-blend-mode:\s*multiply[^"]*"[^>]*href="([^"]+)"[^>]*\/?>/i)
                          || svgString.match(/<image[^>]*href="([^"]+)"[^>]*style="[^"]*mix-blend-mode:\s*multiply[^"]*"[^>]*\/?>/i);
          var textureDataUri = null;

          if (textureMatch) {
            textureDataUri = textureMatch[1];
            svgString = svgString.replace(/<image[^>]*mix-blend-mode[^>]*\/?>/gi, '');
          }

          if (!svgString.includes('width=') && !svgString.includes('height=')) {
            svgString = svgString.replace('<svg', '<svg width="' + dimensions + '" height="' + dimensions + '"');
          }

          svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          objectURL = URL.createObjectURL(svgBlob);

          var img = new Image();

          var cleanup = function() {
            if (objectURL) { self.memoryUtils.revokeObjectURL(objectURL); objectURL = null; }
            if (svgBlob) { self.memoryUtils.cleanupBlob(svgBlob); svgBlob = null; }
            if (canvas) {
              setTimeout(function() { self.memoryUtils.cleanupCanvas(canvas); canvas = null; }, self._pipelineConfig.memoryManagement.cleanupDelay);
            }
          };

          img.onload = function() {
            try {
              canvas = document.createElement('canvas');
              canvas.width = dimensions;
              canvas.height = dimensions;
              var ctx = canvas.getContext('2d');

              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, dimensions, dimensions);
              ctx.drawImage(img, 0, 0, dimensions, dimensions);

              var finalizeCanvas = function() {
                var quality = memoryPressure === 'high' ? 0.7 : 0.95;
                canvas.toBlob(function(blob) {
                  cleanup();
                  if (blob) resolve(blob); else reject(new Error('Failed to create blob from SVG'));
                }, 'image/png', quality);
              };

              if (textureDataUri) {
                var textureImg = new Image();
                textureImg.onload = function() {
                  ctx.globalCompositeOperation = 'multiply';
                  ctx.drawImage(textureImg, 0, 0, dimensions, dimensions);
                  ctx.globalCompositeOperation = 'source-over';
                  finalizeCanvas();
                };
                textureImg.onerror = function() { finalizeCanvas(); };
                textureImg.src = textureDataUri;
              } else {
                finalizeCanvas();
              }
            } catch (err) { cleanup(); reject(err); }
          };

          img.onerror = function() { cleanup(); reject(new Error('Failed to load SVG as image')); };
          img.src = objectURL;
        } catch (err) {
          if (objectURL) self.memoryUtils.revokeObjectURL(objectURL);
          if (svgBlob) self.memoryUtils.cleanupBlob(svgBlob);
          reject(err);
        }
      });
    },

    gatherMetadata: function(lineItemId, selectedLogoId) {
      var metadata = {
        timestamp: new Date().toISOString(),
        url: window.location.href
      };

      if (lineItemId) metadata.lineItemId = lineItemId;
      if (selectedLogoId) metadata.selectedLogoId = selectedLogoId;

      try {
        var productJson = document.querySelector('[data-product-json]');
        if (productJson) {
          var product = JSON.parse(productJson.textContent);
          metadata.productId = product.id;
          metadata.productTitle = product.title;
          metadata.productHandle = product.handle;
        }
        var variantInput = document.querySelector('[name="id"]');
        if (variantInput) metadata.variantId = variantInput.value;
        var quantityInput = document.querySelector('[name="quantity"]');
        if (quantityInput) metadata.quantity = quantityInput.value;
        if (window.Shopify && window.Shopify.cartToken) metadata.cartToken = window.Shopify.cartToken;
        if (window.Shopify && window.Shopify.customer) metadata.customerId = window.Shopify.customer.id;
      } catch (e) {}

      return metadata;
    },

    captureFromProductPage: async function(cartResponse) {
      if (this._isCapturing) return;
      this._isCapturing = true;

      var self = this;

      setTimeout(async function() {
        try {
          var device = self.detectDevice();
          var adaptiveConfig = self.getAdaptiveConfig();

          await new Promise(function(resolve) { setTimeout(resolve, adaptiveConfig.captureDelay); });

          if (device.isMobile && device.performanceClass === 'low') {
            await new Promise(function(resolve) { setTimeout(resolve, 200); });
          }

          if (device.isMobile) {
            var images = document.querySelectorAll('.product__media img');
            var imagePromises = Array.from(images).map(function(img) {
              if (img.complete) return Promise.resolve();
              return new Promise(function(resolve) {
                var timeout = setTimeout(resolve, 1000);
                img.onload = function() { clearTimeout(timeout); resolve(); };
                img.onerror = function() { clearTimeout(timeout); resolve(); };
              });
            });
            if (imagePromises.length > 0) await Promise.all(imagePromises);
          }

          var cartToken = null;
          try {
            var fullCart = await fetch('/cart.js').then(function(r) { return r.json(); });
            cartToken = fullCart.token ? fullCart.token.split('?')[0] : fullCart.token;
          } catch (e) {}

          var productWrapper = self.getSelectedImageWrapper();
          var productBlob = null;

          if (productWrapper) {
            try {
              if (productWrapper.offsetWidth === 0 || productWrapper.offsetHeight === 0) {
                throw new Error('Element has no dimensions');
              }
              productBlob = await self.captureElement(productWrapper);
            } catch (captureError) {
              try {
                var fallback = productWrapper.closest('.product__media-gallery') ||
                              productWrapper.closest('.product-media') ||
                              productWrapper.parentElement;
                if (fallback) productBlob = await self.captureElement(fallback);
              } catch (e) {}
            }
          }

          var logoElement = self.getSelectedLogoElement();
          var logoBlob = null;
          if (logoElement) {
            try {
              logoBlob = await self.captureLogoSvg(logoElement);
            } catch (e) {}
          }

          var logoInput = document.querySelector(self._pipelineConfig.logoSelectorInput);
          var selectedLogoId = logoInput ? logoInput.getAttribute('data-logo-id') : null;

          var metadata = self.gatherMetadata(cartResponse.key, selectedLogoId);
          if (cartToken) metadata.cartToken = cartToken;
          if (cartResponse.key) metadata.lineItemKey = cartResponse.key;

          if (cartResponse.properties) {
            if (cartResponse.properties._product_image_url) {
              metadata.productImageUrl = cartResponse.properties._product_image_url;
              metadata.screenshotProductUrl = cartResponse.properties._product_image_url
                .replace('https://assets.tee.digital/productMockups/', '')
                .replace(/\?key=[^_]+/, '');
            }
            if (cartResponse.properties._logo_url) {
              metadata.logoUrl = cartResponse.properties._logo_url;
              metadata.screenshotLogoUrl = cartResponse.properties._logo_url
                .replace('https://assets.tee.digital/logoMockups/', '')
                .replace(/\?key=[^_]+/, '');
            }
            if (cartResponse.properties._storefront_code) {
              metadata.storefrontCode = cartResponse.properties._storefront_code;
            }

            // Fallback: read from _customizations order attribute when line item properties aren't present
            if (!metadata.productImageUrl && cartResponse.properties._customization_key && fullCart && fullCart.attributes) {
              try {
                var customizations = typeof fullCart.attributes._customizations === 'string'
                  ? JSON.parse(fullCart.attributes._customizations) : fullCart.attributes._customizations;
                var custData = customizations && customizations[cartResponse.properties._customization_key];
                if (custData && custData.locations && custData.locations[0]) {
                  var loc = custData.locations[0];
                  if (loc.productImage) {
                    metadata.productImageUrl = loc.productImage;
                    metadata.screenshotProductUrl = loc.productImage
                      .replace('https://assets.tee.digital/productMockups/', '')
                      .replace(/\?key=[^_]+/, '');
                  }
                  if (loc.logo && loc.logo.url) {
                    metadata.logoUrl = loc.logo.url;
                    metadata.screenshotLogoUrl = loc.logo.url
                      .replace('https://assets.tee.digital/logoMockups/', '')
                      .replace(/\?key=[^_]+/, '');
                  }
                  if (custData.metadata && custData.metadata.storefront) {
                    metadata.storefrontCode = custData.metadata.storefront;
                  }
                }
              } catch (e) {}
            }
          }

          metadata.lineItemVariantId = cartResponse.variant_id;
          metadata.lineItemProductId = cartResponse.product_id;
          metadata.lineItemQuantity = cartResponse.quantity;
          metadata.lineItemPrice = cartResponse.price;

          metadata.deviceInfo = {
            type: device.isMobile ? 'mobile' : (device.isTablet ? 'tablet' : 'desktop'),
            performanceClass: device.performanceClass,
            viewport: device.viewport
          };

          if (productBlob || logoBlob) {
            await self.sendToWebhookPipeline(
              { product: productBlob, logo: logoBlob },
              metadata
            );
          } else {
            try {
              await self.sendToWebhookPipeline(
                { product: null, logo: null },
                { ...metadata, captureStatus: 'failed', reason: 'No screenshots captured' }
              );
            } catch (e) {}
          }

        } catch (error) {
        } finally {
          var resetDelay = self.detectDevice().isMobile ? 1500 : 1000;
          setTimeout(function() {
            self._isCapturing = false;
            if (self.detectDevice().isMobile && self._pipelineConfig.memoryManagement.enableGC) {
              setTimeout(function() { self.memoryUtils.forceGarbageCollection(); }, 500);
            }
          }, resetDelay);
        }
      }, 100);
    },

    sendToWebhookPipeline: async function(screenshots, metadata) {
      var webhookUrl = window.SCREENSHOT_WEBHOOK_URL;
      var maxRetries = 3;
      var formData = new FormData();

      if (screenshots.product) {
        var productFilename = metadata.screenshotProductUrl || 'product-customization.png';
        formData.append('productImage', screenshots.product, productFilename);
      }

      if (screenshots.logo) {
        var logoFilename = metadata.screenshotLogoUrl || 'logo-selection.png';
        formData.append('logoImage', screenshots.logo, logoFilename);
      } else {
        var canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        var placeholder = await new Promise(function(resolve) {
          canvas.toBlob(function(blob) { resolve(blob); }, 'image/png');
        });
        if (placeholder) {
          formData.append('logoImage', placeholder, 'no-logo.png');
        }
        metadata.logoImagePlaceholder = true;
      }

      formData.append('metadata', JSON.stringify(metadata));

      var lastError = null;

      for (var attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          var controller = new AbortController();
          var device = this.detectDevice();
          var timeoutMs = device.isMobile ? 30000 : 15000;
          var timeoutId = setTimeout(function() { controller.abort(); }, timeoutMs);

          var response = await fetch(webhookUrl, {
            method: 'POST',
            body: formData,
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error('Webhook failed: ' + response.status + ' ' + response.statusText);
          }

          return await response.json();

        } catch (error) {
          lastError = error;

          if (error.message.includes('400') || error.message.includes('401')) {
            break;
          }

          if (attempt < maxRetries) {
            var delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            await new Promise(function(resolve) { setTimeout(resolve, delay); });
          }
        }
      }

      return null;
    },

    sendToWebhook: async function(locationCaptures, options = {}) {
      const {
        webhookUrl = window.SCREENSHOT_WEBHOOK_URL,
        metadata = {},
        locationUrls = {},
        maxRetries = 3
      } = options;

      const formData = new FormData();

      locationCaptures.forEach((capture, index) => {
        const { locationId, location, locState, blobs } = capture;
        const urlData = locationUrls[locationId] || {};
        const filenames = urlData.filenames || {};

        if (blobs.fullMockup) {
          formData.append(
            `location_${index}_fullMockup`,
            blobs.fullMockup,
            filenames.mockup || `${locationId}_mockup.png`
          );
        }

        if (blobs.logo) {
          formData.append(
            `location_${index}_logo`,
            blobs.logo,
            filenames.logo || `${locationId}_logo.png`
          );
        }

        if (blobs.text) {
          formData.append(
            `location_${index}_text`,
            blobs.text,
            filenames.text || `${locationId}_text.png`
          );
        }

        formData.append(`location_${index}_metadata`, JSON.stringify({
          locationId: locationId,
          locationName: location.name,
          logoId: locState.selectedLogo?.id,
          personalizationText: locState.personalization?.text,
          threadColors: locState.logoThreadColors,
          screenshotProductUrl: filenames.mockup,
          screenshotLogoUrl: filenames.logo,
          screenshotTextUrl: filenames.text
        }));
      });

      formData.append('metadata', JSON.stringify({
        ...metadata,
        locationCount: locationCaptures.length
      }));

      let lastError = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const controller = new AbortController();
          const device = this.detectDevice();
          const timeoutMs = device.isMobile ? 30000 : 15000;
          const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

          const response = await fetch(webhookUrl, {
            method: 'POST',
            body: formData,
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Webhook failed: ${response.status} ${response.statusText}`);
          }

          return await response.json();

        } catch (error) {
          lastError = error;

          if (error.message.includes('400') || error.message.includes('401')) {
            break;
          }

          if (attempt < maxRetries) {
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      return null;
    },

    init: function() {
      if (typeof fabric !== 'undefined') return;

      var self = this;
      var device = this.detectDevice();

      document.addEventListener('cartItemAdded', function(e) {
        if (!e.detail) return;

        if (device.performanceClass === 'low') {
          setTimeout(function() { self.captureFromProductPage(e.detail); }, 100);
        } else {
          self.captureFromProductPage(e.detail);
        }
      });

      if (device.isMobile) {
        window.addEventListener('orientationchange', function() {
          self._deviceInfo = null;
        });
        window.addEventListener('pagehide', function() {
          self.memoryUtils.forceGarbageCollection();
        });
      }
    }
  };

  window.MultiLocationScreenshotService = MultiLocationScreenshotService;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      MultiLocationScreenshotService.init();
    });
  } else {
    MultiLocationScreenshotService.init();
  }

})();
